/**
 * PATCH /leave/settings sends only what the admin actually changed
 * (QA 2026-10 leave #14).
 *
 * The leave-policy card used to PATCH every field it had loaded. The
 * weekly off is shared with Settings → Attendance (one tenant column), so
 * a card loaded before someone changed the weekly off there wrote the stale
 * mask straight back over it on its next save — and the same goes for any
 * other field edited in another tab. Diffing the edited values against the
 * last-known server state means an untouched field is never sent, so it can
 * never overwrite a newer value.
 *
 * Plain .ts (no JSX) so `npm run test:unit` can load it.
 */

import type { LeaveSettings, UpdateLeaveSettingsRequest } from "@/types/leave";

/** The editable leave-settings fields (everything but current_leave_year). */
export const EDITABLE_LEAVE_SETTINGS = [
  "leave_year_start_month",
  "comp_off_expiry_days",
  "probation_months",
  "comp_off_auto_earn",
  "weekly_off_mask",
  "optional_holidays_per_year",
] as const satisfies readonly (keyof UpdateLeaveSettingsRequest)[];

/**
 * The subset of `edited` whose value differs from `loaded` (the settings
 * the form was showing, i.e. the last-known server state). Fields absent
 * from `edited` are never sent. With no `loaded` snapshot (nothing to
 * compare against) every edited field is sent unchanged — the server still
 * validates each one.
 */
export function changedLeaveSettings(
  loaded: Partial<LeaveSettings> | undefined,
  edited: UpdateLeaveSettingsRequest,
): UpdateLeaveSettingsRequest {
  if (!loaded) return { ...edited };
  const out: UpdateLeaveSettingsRequest = {};
  for (const key of EDITABLE_LEAVE_SETTINGS) {
    const next = edited[key];
    if (next === undefined) continue;
    if (loaded[key] === next) continue;
    // Each key's value type matches between the two shapes.
    (out as Record<string, unknown>)[key] = next;
  }
  return out;
}

/** True when the diff carries nothing to save. */
export function isEmptySettingsPatch(p: UpdateLeaveSettingsRequest): boolean {
  return Object.keys(p).length === 0;
}
